import { memo, useCallback, useMemo, useState } from "react";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import MarkEmailReadOutlinedIcon from "@mui/icons-material/MarkEmailReadOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import {
  tableActionCellStyles,
  tableActionHeaderCellStyles,
  tableContainerStyles,
  tableDataCellStyles,
  tableHeaderCellStyles,
  tableRowStyles,
} from "@/components/GenericTable/styles";

import {
  compareCommunicationsDateTimeDesc,
  formatCommunicationsDateTimeDisplay,
} from "../helpers/formatCrmCommunicationsDisplay";
import {
  deriveBatchTaskSendStatus,
} from "../helpers/formatBatchTaskDisplay";
import { useTablePagination } from "../hooks/useTablePagination";
import type { CrmPatientSummary, FluxZapBatchTaskView } from "../types";
import type { OnOpenCommunicationsWhatsAppSend } from "../types/sendMessage";
import BatchTaskMetricCell from "./BatchTaskMetricCell";
import BatchTaskSendStatusChip from "./BatchTaskSendStatusChip";
import CommunicationsTablePagination from "./CommunicationsTablePagination";
import CommunicationsBatchTaskDetailDialog from "./CommunicationsBatchTaskDetailDialog";
import CommunicationsSubsectionPanel from "./CommunicationsSubsectionPanel";

export interface CommunicationsBatchHistoryPanelProps {
  batchTasks: FluxZapBatchTaskView[];
  patientLookup: Map<string, CrmPatientSummary>;
  vetApp?: boolean;
  petNamesByTutorLogicalId?: Map<string, string[]>;
  onOpenSendMessage: OnOpenCommunicationsWhatsAppSend;
  onRefresh?: () => void;
  isRefreshing?: boolean;
}

const batchHistoryBodyCellSx = {
  ...tableDataCellStyles,
  verticalAlign: "middle" as const,
  py: 2,
};

const batchHistoryMetricHeaderSx = {
  display: "inline-flex",
  alignItems: "center",
  gap: 0.5,
};

const batchHistoryReceivedHeaderIconSx = {
  fontSize: "0.95rem",
  color: "text.secondary",
};

const batchHistoryFailuresHeaderIconSx = {
  fontSize: "0.95rem",
  color: "error.main",
};

/**
 * Histórico de disparos (Task BATCH_NOTIFICATION), inspirado no painel de estatísticas
 * porém com tabela acionável e ligação explícita a Basic (focus) e destinatários.
 */
const CommunicationsBatchHistoryPanel = memo(
  ({
    batchTasks,
    patientLookup,
    vetApp = false,
    petNamesByTutorLogicalId,
    onOpenSendMessage,
    onRefresh,
    isRefreshing,
  }: CommunicationsBatchHistoryPanelProps) => {
    const [detailTask, setDetailTask] = useState<FluxZapBatchTaskView | null>(
      null,
    );

    const sortedTasks = useMemo(
      () =>
        [...batchTasks].sort((a, b) =>
          compareCommunicationsDateTimeDesc(a.authoredOn, b.authoredOn),
        ),
      [batchTasks],
    );

    const batchHistoryResetKey = useMemo(
      () =>
        sortedTasks.length > 0
          ? `${sortedTasks.length}-${sortedTasks[0]?.taskId ?? ""}`
          : "empty",
      [sortedTasks],
    );

    const {
      paginatedItems: paginatedTasks,
      page,
      rowsPerPage,
      totalCount: batchTaskCount,
      handlePageChange,
      handleRowsPerPageChange,
    } = useTablePagination(sortedTasks, batchHistoryResetKey);

    const closeDetail = useCallback(() => setDetailTask(null), []);

    return (
      <>
        <CommunicationsSubsectionPanel
          panelId="communications-batch-history"
          defaultExpanded
          icon={<HistoryOutlinedIcon fontSize="small" />}
          title="Extrato de envios de mensagens"
          subtitle="Mensagens enviadas em lote ou individualmente."
          onRefresh={onRefresh}
          isRefreshing={isRefreshing}
        >
          <Stack spacing={1.25}>
            {sortedTasks.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                Nenhum disparo registrado até o momento.
              </Typography>
            ) : (
              <TableContainer sx={tableContainerStyles}>
                <Table size="small" aria-label="Histórico de disparos em lote">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={tableHeaderCellStyles} align="center">
                        <Typography variant="subtitle2">
                          Modelo de mensagem
                        </Typography>
                      </TableCell>
                      <TableCell sx={tableHeaderCellStyles} align="center">
                        <Typography variant="subtitle2">
                          Data do envio
                        </Typography>
                      </TableCell>
                      <TableCell sx={tableHeaderCellStyles} align="center">
                        <Typography variant="subtitle2">
                          Status do envio
                        </Typography>
                      </TableCell>
                      <TableCell sx={tableHeaderCellStyles} align="center">
                        <Box
                          component="span"
                          sx={batchHistoryMetricHeaderSx}
                        >
                          <MarkEmailReadOutlinedIcon
                            sx={batchHistoryReceivedHeaderIconSx}
                            aria-hidden
                          />
                          <Typography variant="subtitle2">Recebidos</Typography>
                        </Box>
                      </TableCell>
                      <TableCell sx={tableHeaderCellStyles} align="center">
                        <Box
                          component="span"
                          sx={batchHistoryMetricHeaderSx}
                        >
                          <BlockOutlinedIcon
                            sx={batchHistoryFailuresHeaderIconSx}
                            aria-hidden
                          />
                          <Typography variant="subtitle2">Falhas</Typography>
                        </Box>
                      </TableCell>
                      <TableCell
                        sx={tableActionHeaderCellStyles}
                        align="center"
                      >
                        <Typography variant="subtitle2">Ações</Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedTasks.map((task) => {
                      const sendStatus = deriveBatchTaskSendStatus(task);

                    return (
                      <TableRow key={task.taskId} hover sx={tableRowStyles}>
                        <TableCell align="center" sx={batchHistoryBodyCellSx}>
                          <Typography variant="body2" fontWeight={600}>
                            {task.templateLabel}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={batchHistoryBodyCellSx}>
                          {formatCommunicationsDateTimeDisplay(task.authoredOn)}
                        </TableCell>
                        <TableCell align="center" sx={batchHistoryBodyCellSx}>
                          <BatchTaskSendStatusChip status={sendStatus} />
                        </TableCell>
                        <TableCell align="center" sx={batchHistoryBodyCellSx}>
                          <BatchTaskMetricCell task={task} kind="received" />
                        </TableCell>
                        <TableCell align="center" sx={batchHistoryBodyCellSx}>
                          <BatchTaskMetricCell task={task} kind="failures" />
                        </TableCell>
                        <TableCell align="center" sx={tableActionCellStyles}>
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "center",
                            }}
                          >
                            <Tooltip title="Visualizar detalhes do envio">
                              <IconButton
                                size="small"
                                aria-label={`Visualizar detalhes do disparo ${task.taskId}`}
                                onClick={() => setDetailTask(task)}
                              >
                                <VisibilityOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <CommunicationsTablePagination
              count={batchTaskCount}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handleRowsPerPageChange}
            />
          </Stack>
        </CommunicationsSubsectionPanel>

        {detailTask && (
          <CommunicationsBatchTaskDetailDialog
            open={detailTask != null}
            task={detailTask}
            patientLookup={patientLookup}
            vetApp={vetApp}
            petNamesByTutorLogicalId={petNamesByTutorLogicalId}
            onClose={closeDetail}
            onOpenSendMessage={onOpenSendMessage}
          />
        )}
      </>
    );
  },
);

CommunicationsBatchHistoryPanel.displayName = "CommunicationsBatchHistoryPanel";

export default CommunicationsBatchHistoryPanel;
